// user.component.ts
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    ViewEncapsulation,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';

import { AccountService } from 'app/modules/admin/parametre/account/account.service';
import { AuthService } from 'app/core/auth/auth.service';
import { BooleanInput } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { NgOptimizedImage } from '@angular/common';
import { User } from 'app/core/user/user.types';
import { UserService } from 'app/_services/api_from_back/user.service';
import { clubConfig } from 'config/club.config';
import { finalize } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatDividerModule,
        MatIconModule,
        MatMenuModule,
        RouterModule,
        TranslocoModule,
        NgOptimizedImage,
    ],
})
export class UserComponent {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    // Injecter les services
    private _authService = inject(AuthService);
    private _router = inject(Router);
    private _apiUserService = inject(UserService);
    private _accountService = inject(AccountService);
    private _translocoService = inject(TranslocoService);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    // Inputs
    @Input() showAvatar: boolean = true;

    // Signals
    private _userProfile = signal<any>(null);
    userProfile = this._userProfile.asReadonly();

    // Convertir account service observable en signal
    private _user$ = this._accountService.user$;

    // Pour les langues
    activeLang = toSignal(this._translocoService.langChanges$, {
        initialValue: this._translocoService.getActiveLang(),
    });

    availableLangsSignal = signal([
        { id: 'en', label: 'English-US' },
        { id: 'fr', label: 'Français' },
        { id: 'it', label: 'Italiano' },
        { id: 'de', label: 'Deutsch' },
    ]);
    availableLangs = this.availableLangsSignal.asReadonly();

    // Mapping des codes de drapeaux pour les langues
    flagCodes: { [key: string]: string } = {
        en: 'en',
        fr: 'fr',
        it: 'it',
        de: 'de',
    };

    // Pays francophones européens - mapping nom pays -> code drapeau
    francophoneCountries: { [key: string]: string } = {
        Suisse: 'ch',
        Switzerland: 'ch',
        Belgique: 'be',
        Belgium: 'be',
        Luxembourg: 'lu',
        Monaco: 'mc',
    };

    // Signal computed pour déterminer le drapeau du pays francophone
    countryFlagForFrench = computed(() => {

        const clubCountry = this.getClubCountry();
        const activeLang = this.activeLang();

        if (activeLang === 'fr' && clubCountry) {
            const countryCode = this.francophoneCountries[clubCountry];
            return countryCode || null;
        }
        return null;
    });

    constructor() {
        effect(
            () => {
                this._user$.subscribe((user) => {
                    if (user) {
                        this._userProfile.set(user);
                    } else {
                        this._accountService
                            .getConnectedUser()
                            .subscribe((data) => {
                                this._userProfile.set(data);
                                this._accountService.setUser(data);
                            });
                    }
                });
            },
            { allowSignalWrites: true }
        );
    }


    /**
     * Récupère le pays du club depuis la configuration
     * Pour l'instant, on prend le premier club, mais vous pouvez adapter selon votre logique
     */
    getClubCountry(): string | null {

        return clubConfig[0]?.country || null;
    }

    /**
     * Récupère les drapeaux à afficher pour une langue donnée
     * Pour le français des pays francophones, retourne à la fois le drapeau français et celui du pays
     */
    getFlagsForLanguage(langId: string): { main: string; country?: string } {
        const mainFlag = this.flagCodes[langId];
        if (langId === 'fr') {
            const countryFlag = this.countryFlagForFrench();
            if (countryFlag) {
                return { main: mainFlag, country: countryFlag };
            }
        }

        return { main: mainFlag };
    }

    /**
     * Set the active lang
     */
    setActiveLang(lang: string): void {
        this._translocoService.setActiveLang(lang);
    }

    /**
     * Update the user status
     */
    updateUserStatus(status: string): void {
        const currentUser = this._userProfile();
        if (!currentUser || !currentUser.id) {
            console.error(
                'Utilisateur non trouvé pour la mise à jour du statut'
            );
            return;
        }

        const updateData = { status };

        this._accountService.updateGuest(currentUser.id, updateData).subscribe({
            next: (updatedUser) => {
                this._userProfile.set(updatedUser);
                this._accountService.setUser(updatedUser);
            },
            error: (error) => {
                console.error(
                    'Erreur lors de la mise à jour du statut :',
                    error
                );
            },
        });
    }

    /**
     * Delete user account
     */
    onDeleteAccount(): void {
        const confirmation = this._fuseConfirmationService.open({
            title: 'Suppression du compte',
            message:
                'ATTENTION: Cette action est irréversible !<br><br>Voulez-vous vraiment supprimer définitivement votre compte ?<br><strong>Toutes vos données seront perdues.</strong>',
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation-triangle',
                color: 'warn',
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Supprimer définitivement',
                    color: 'warn',
                },
                cancel: {
                    show: true,
                    label: 'Annuler',
                },
            },
        });

        confirmation.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                const doubleConfirmation = this._fuseConfirmationService.open({
                    title: 'Dernière confirmation',
                    message:
                        'Êtes-vous absolument certain de vouloir supprimer votre compte ?<br><br><strong>Cette action ne peut pas être annulée.</strong>',
                    icon: {
                        show: true,
                        name: 'heroicons_outline:trash',
                        color: 'warn',
                    },
                    actions: {
                        confirm: {
                            show: true,
                            label: 'Oui, supprimer',
                            color: 'warn',
                        },
                        cancel: {
                            show: true,
                            label: 'Non, conserver',
                        },
                    },
                });

                doubleConfirmation.afterClosed().subscribe((finalResult) => {
                    if (finalResult === 'confirmed') {
                        this.deleteUserAccount();
                    }
                });
            }
        });
    }

    /**
     * Perform actual account deletion
     */
    private deleteUserAccount(): void {
        const currentUserId = this.getCurrentUserId();

        if (!currentUserId) {
            this._authService.getProfil();
            setTimeout(() => {
                const retryUserId = this.getCurrentUserId();
                if (retryUserId) {
                    this.performAccountDeletion(retryUserId);
                } else {
                    this.showErrorMessage({
                        message:
                            'ID utilisateur non trouvé. Veuillez vous reconnecter.',
                    });
                }
            }, 500);
            return;
        }

        this.performAccountDeletion(currentUserId);
    }

    /**
     * Execute account deletion
     */
    private performAccountDeletion(userId: string): void {
        console.log(
            "Suppression du compte en cours pour l'utilisateur:",
            userId
        );

        this._apiUserService
            .userControllerSoftDelete({ id: userId })
            .pipe(
                finalize(() => {
                    console.log('Requête de suppression terminée');
                })
            )
            .subscribe({
                next: (response) => {
                    console.log('Compte supprimé avec succès', response);
                    this.showSuccessMessage();
                },
                error: (error) => {
                    console.error(
                        'Erreur lors de la suppression du compte',
                        error
                    );
                    this.showErrorMessage(error);
                },
            });
    }

    /**
     * Get current user ID
     */
    private getCurrentUserId(): string | null {
        try {
            const currentUser = this._userProfile();
            if (currentUser?.id) {
                console.log('mon user', currentUser);
                return currentUser.id;
            }

            const userData =
                localStorage.getItem('user') || sessionStorage.getItem('user');
            if (userData) {
                console.log('mon user storage', userData);
                const user = JSON.parse(userData);
                return user?.id || null;
            }

            console.error("Impossible de récupérer l'ID utilisateur");
            return null;
        } catch (error) {
            console.error(
                "Erreur lors de la récupération de l'ID utilisateur:",
                error
            );
            return null;
        }
    }

    /**
     * Show success message after account deletion
     */
    private showSuccessMessage(): void {
        const successDialog = this._fuseConfirmationService.open({
            title: 'Compte supprimé',
            message:
                "Votre compte a été supprimé avec succès.<br>Vous allez être redirigé vers la page d'accueil.",
            icon: {
                show: true,
                name: 'heroicons_outline:check-circle',
                color: 'success',
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'OK',
                    color: 'primary',
                },
                cancel: {
                    show: false,
                },
            },
        });

        successDialog.afterClosed().subscribe(() => {
            this._authService.signOut().subscribe({
                next: () => {
                    this._router.navigate(['/sign-out']);
                },
                error: () => {
                    this._router.navigate(['/sign-out']);
                },
            });
        });
    }

    /**
     * Show error message
     */
    private showErrorMessage(error: any): void {
        let errorMessage = "Une erreur inattendue s'est produite.";

        if (error?.error?.message) {
            errorMessage = error.error.message;
        } else if (error?.message) {
            errorMessage = error.message;
        }

        this._fuseConfirmationService.open({
            title: 'Erreur',
            message: `Impossible de supprimer le compte :<br><strong>${errorMessage}</strong><br><br>Veuillez réessayer ou contacter le support.`,
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation-circle',
                color: 'warn',
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'OK',
                    color: 'primary',
                },
                cancel: {
                    show: false,
                },
            },
        });
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._router.navigate(['/sign-out']);
    }
}
